import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";

// North has no npm SDK — the Embedded Checkout script must be loaded at runtime.
const NORTH_CHECKOUT_SCRIPT_SRC = "https://checkout.north.com/checkout.js";
const NORTH_MOUNT_TIMEOUT_MS = 10000;
const NORTH_CONTAINER_ID = "north-checkout-container";

let northCheckoutScriptPromise = null;
// Loads checkout.js once and resolves with the global `checkout` SDK object.
const loadNorthCheckoutSdk = () => {
  if (northCheckoutScriptPromise) return northCheckoutScriptPromise;

  northCheckoutScriptPromise = new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.checkout) {
      resolve(window.checkout);
      return;
    }

    const existing = document.querySelector(
      `script[src="${NORTH_CHECKOUT_SCRIPT_SRC}"]`
    );
    const onLoad = () => {
      if (window.checkout) resolve(window.checkout);
      else reject(new Error("North checkout.js loaded but `checkout` global is missing."));
    };

    if (existing) {
      if (window.checkout) resolve(window.checkout);
      else existing.addEventListener("load", onLoad, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = NORTH_CHECKOUT_SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Failed to load North checkout.js")),
      { once: true }
    );
    document.head.appendChild(script);
  });

  return northCheckoutScriptPromise;
};

const darkColors = {
  background: "#121212",
  paper: "#1e1e1e",
  text: "#ffffff",
  textSecondary: "#b0b0b0",
  primary: "#90caf9",
  border: "#333333",
  surface: "#1e1e1e",
  surfaceAlt: "#2a2a2a",
};

const lightColors = {
  background: "#ffffff",
  paper: "#f8f9fa",
  text: "#212529",
  textSecondary: "#6c757d",
  primary: "#1976d2",
  border: "#dee2e6",
  surface: "#f8f9fa",
  surfaceAlt: "#eef2f7",
};

/**
 * NorthFundWalletPopup — common/reusable North Embedded Checkout (Fields mode)
 * popup for the "Fund Wallet" card-funding flow. Mounts North's hosted
 * checkout.js payment fields into this dialog using the short-lived session
 * `sessionToken` created server-side via /transaction/generateEpxNorthPaymentIntent
 * (see PabtekPaymentPopup.js' handleNorthProceed). The actual PRIVATE_API_KEY /
 * CHECKOUT_ID / PROFILE_ID never reach the browser — only the resulting session
 * token does, exactly like the Stripe clientSecret flow next to it. Unlike
 * Stripe (which auto-confirms on click) or North's Form mode (which completes
 * inside the iframe and fires onPaymentComplete), Fields mode requires an
 * explicit checkout.submit() call once the fields are filled in — that's what
 * the "Fund Wallet" button below does.
 *
 * Props:
 *   open          - boolean
 *   onClose       - function — close without completing payment
 *   onSuccess     - function(submitResponse) — called once checkout.submit() resolves
 *   theme         - "light" | "dark"
 *   currencyCode  - string, e.g. "USDC" — wallet asset credited
 *   sessionToken  - string — the North checkout session token
 *   amountUsd     - number — total USD amount charged to the card
 *   cryptoAmount  - number — amount of currencyCode credited to the wallet
 *   previewOnly   - boolean — TODO(North): temporary test flag. When true, skips
 *                   loading/mounting the real checkout.js SDK and shows a
 *                   placeholder instead, so the popup shell can be reviewed as a
 *                   fallback if session creation fails. Remove this prop once
 *                   North has been fully validated end-to-end.
 */
const NorthFundWalletPopup = ({
  open,
  onClose,
  onSuccess,
  theme = "light",
  currencyCode,
  sessionToken,
  amountUsd,
  cryptoAmount,
  previewOnly = false,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isDark = theme === "dark";
  const colors = isDark ? darkColors : lightColors;

  const [mounting, setMounting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [fieldsReady, setFieldsReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const mountedTokenRef = useRef(null);
  const checkoutSdkRef = useRef(null);

  useEffect(() => {
    // TODO(North): remove this early-return once previewOnly is removed — it skips
    // the real SDK load/mount entirely since there is no mounted session to submit.
    if (previewOnly) return;
    if (!open) {
      mountedTokenRef.current = null;
      checkoutSdkRef.current = null;
      setFieldsReady(false);
      return;
    }
    if (!sessionToken || mountedTokenRef.current === sessionToken) return;

    let cancelled = false;
    setMounting(true);
    setFieldsReady(false);
    setErrorMsg("");

    loadNorthCheckoutSdk()
      .then((checkoutSdk) => {
        if (cancelled) return checkoutSdk;
        checkoutSdkRef.current = checkoutSdk;

        // Fields mode: mount renders the payment fields; the "Fund Wallet"
        // button below calls checkout.submit() explicitly (no auto-complete).
        return Promise.race([
          checkoutSdk.mount(sessionToken, NORTH_CONTAINER_ID),
          new Promise((_, reject) =>
            setTimeout(
              () =>
                reject(
                  new Error(
                    "The North payment form did not load in time. Please try again."
                  )
                ),
              NORTH_MOUNT_TIMEOUT_MS
            )
          ),
        ]);
      })
      .then(() => {
        if (cancelled) return;
        mountedTokenRef.current = sessionToken;
        setFieldsReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMsg(
          err?.message || "Unable to load the North payment form. Please try again."
        );
      })
      .finally(() => {
        if (!cancelled) setMounting(false);
      });

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [open, sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (processing) return;
    onClose();
  };

  const handlePay = async () => {
    const checkoutSdk = checkoutSdkRef.current;
    if (!checkoutSdk || typeof checkoutSdk.submit !== "function" || !fieldsReady) {
      setErrorMsg("Payment form is not ready yet. Please wait a moment and try again.");
      return;
    }

    setProcessing(true);
    setErrorMsg("");
    try {
      const resp = await checkoutSdk.submit();
      if (typeof onSuccess === "function") onSuccess(resp);
    } catch (err) {
      setErrorMsg(err?.message || "Payment failed. Please try again.");
      setProcessing(false);
    }
  };

  const logoUrl = `https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/${(
    currencyCode || ""
  ).toUpperCase()}.png`;
  void logoUrl; // reserved for parity with StripeFundWalletPopup's summary rows

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          backgroundColor: colors.background,
          color: colors.text,
          boxShadow: isDark
            ? "0 24px 64px rgba(0,0,0,0.6)"
            : "0 24px 64px rgba(0,0,0,0.12)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: isMobile ? "100vh" : "92vh",
        },
      }}
    >
      {/* ── Gradient header band ─────────────────────────────── */}
      <Box
        sx={{
          background: isDark
            ? "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)"
            : "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
          px: 3,
          pt: 3,
          pb: 3,
          position: "relative",
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", color: "#fff" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CreditCardIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
            >
              Fund Your Wallet
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
              Secure Card Payment via North
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Scrollable content ───────────────────────────────── */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 0, overflowY: "auto", flex: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Summary */}
          <Box
            sx={{
              borderRadius: "12px",
              border: `1px solid ${colors.border}`,
              overflow: "hidden",
            }}
          >
            {[
              {
                label: "Total Card Charge",
                value: `$${(amountUsd || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} USD`,
                highlight: true,
              },
              {
                label: "Wallet Asset Credited",
                value: `${(cryptoAmount || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })} ${currencyCode}`,
              },
              {
                label: "Wallet",
                value: `${currencyCode} Wallet`,
              },
            ].map((row, i, arr) => (
              <Box
                key={row.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1.1,
                  backgroundColor: row.highlight
                    ? isDark
                      ? "rgba(144,202,249,0.1)"
                      : "rgba(25,118,210,0.07)"
                    : i % 2 === 0
                      ? colors.surfaceAlt
                      : colors.background,
                  borderBottom:
                    i < arr.length - 1 ? `1px solid ${colors.border}` : "none",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary, fontSize: "0.82rem" }}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: row.highlight ? colors.primary : colors.text,
                    fontWeight: row.highlight ? 700 : 600,
                    fontSize: "0.82rem",
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* ── North Embedded Checkout fields ──────────────────── */}
          {/* TODO(North): previewOnly branch is a fallback for when session
              creation fails — shows a placeholder instead of a real mounted
              form. Delete this branch (and the previewOnly prop) once North has
              been fully validated end-to-end. */}
          {previewOnly ? (
            <Box
              sx={{
                border: `1px dashed ${colors.border}`,
                borderRadius: "12px",
                py: 5,
                px: 3,
                textAlign: "center",
                backgroundColor: colors.surfaceAlt,
              }}
            >
              <CreditCardIcon sx={{ fontSize: 32, color: colors.primary, mb: 1 }} />
              <Typography
                variant="body2"
                sx={{ color: colors.text, fontWeight: 600, fontSize: "0.9rem" }}
              >
                North Embedded Checkout (Preview)
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.textSecondary, mt: 0.5, fontSize: "0.78rem" }}
              >
                This is a UI preview. The real North payment fields will mount here
                once a valid checkout session is created.
              </Typography>
            </Box>
          ) : open && sessionToken ? (
            <Box sx={{ position: "relative" }}>
              {mounting && (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress size={22} sx={{ color: colors.primary }} />
                  <Typography
                    variant="body2"
                    sx={{ color: colors.textSecondary, mt: 1, fontSize: "0.82rem" }}
                  >
                    Loading secure payment form…
                  </Typography>
                </Box>
              )}
              {/* North's checkout.mount() renders its payment fields into this element.
                  The "& iframe" rule forces North's iframe to fill this box instead of
                  keeping whatever fixed height it mounts with, which otherwise clips
                  the form and leaves it internally scrollable. */}
              <Box
                id={NORTH_CONTAINER_ID}
                sx={{
                  minHeight: mounting ? 0 : 800,
                  display: mounting ? "none" : "block",
                  "& iframe": {
                    width: "100% !important",
                    height: "100% !important",
                    minHeight: "800px !important",
                    border: "none",
                  },
                }}
              />
            </Box>
          ) : (
            open && (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CircularProgress size={22} sx={{ color: colors.primary }} />
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary, mt: 1, fontSize: "0.82rem" }}
                >
                  Loading secure payment form…
                </Typography>
              </Box>
            )
          )}

          {/* Fields mode requires an explicit submit — checkout.submit() */}
          {!previewOnly && (
            <Button
              onClick={handlePay}
              variant="contained"
              disabled={!fieldsReady || mounting || processing}
              fullWidth
              endIcon={
                processing ? (
                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                ) : undefined
              }
              sx={{
                borderRadius: "12px",
                py: 1.5,
                fontWeight: 700,
                fontSize: "0.95rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                background:
                  fieldsReady && !mounting && !processing
                    ? "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)"
                    : undefined,
                boxShadow:
                  fieldsReady && !mounting && !processing
                    ? "0 4px 16px rgba(25,118,210,0.35)"
                    : "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
                  boxShadow: "0 6px 20px rgba(25,118,210,0.45)",
                },
                "&.Mui-disabled": {
                  backgroundColor: isDark ? "#2a2a2a" : "#e0e0e0",
                  color: isDark ? "#555" : "#9e9e9e",
                },
              }}
            >
              {processing ? "Processing…" : "Fund Wallet"}
            </Button>
          )}

          {errorMsg && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(229,115,115,0.1)" : undefined,
                color: isDark ? "#ef9a9a" : undefined,
                "& .MuiAlert-icon": { color: isDark ? "#ef9a9a" : undefined },
              }}
            >
              {errorMsg}
            </Alert>
          )}

          <Box sx={{ pb: 3 }} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NorthFundWalletPopup;
